import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import HeaderSection from '../../components/home/HeaderSection';
import FooterSection from '../../components/home/FooterSection';
import CourseCard from '../../components/home/CoursesSection/CourseCard';
import CategoryCoursesHero from '../../components/categoryCourses/CategoryCoursesHero';
import CategoryFeaturedCarousel from '../../components/categoryCourses/CategoryFeaturedCarousel';
import SubjectFilterTabs, { ALL_TAB } from '../../components/categoryCourses/SubjectFilterTabs';
import { useCoursesBySubjects } from '../../hooks/useCoursesBySubjects';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import {
  formatCategoryHeading,
  formatHeroTitle,
  normalizeSubjectName,
} from '../../utils/categoryCourses';

const HERO_DESCRIPTION =
  'Our newest course is designed to build strong concepts step by step. Perfectly structured to help you score better and learn with confidence.';

function CategoryCoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [activeSubjectId, setActiveSubjectId] = useState(ALL_TAB);

  const categoryId = searchParams.get('category_id');
  const subcategoryId = searchParams.get('subcategory_id');
  const subjectIdFromUrl = searchParams.get('subject_id');
  const categoryName = location.state?.categoryName || 'Class';
  const subcategoryName = location.state?.subcategoryName || '';

  useEffect(() => {
    setActiveSubjectId(subjectIdFromUrl ? String(subjectIdFromUrl) : ALL_TAB);
  }, [subjectIdFromUrl, categoryId, subcategoryId, location.key]);

  const handleSubjectChange = (subjectId) => {
    const nextId = String(subjectId);
    setActiveSubjectId(nextId);

    const nextParams = new URLSearchParams(searchParams);
    if (nextId === ALL_TAB) {
      nextParams.delete('subject_id');
    } else {
      nextParams.set('subject_id', nextId);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const { subjects, loading: subjectsLoading } = useFilterOptions(categoryId, Boolean(categoryId));

  const subjectIds = useMemo(() => {
    if (!subjects.length) return [];
    if (activeSubjectId === ALL_TAB) {
      return subjects.map((subject) => subject.id);
    }
    return [Number(activeSubjectId)];
  }, [subjects, activeSubjectId]);

  const activeSubjectName = useMemo(() => {
    if (activeSubjectId === ALL_TAB) return '';
    const match = subjects.find((subject) => String(subject.id) === String(activeSubjectId));
    return match ? normalizeSubjectName(match.name) : '';
  }, [subjects, activeSubjectId]);

  const { courses, loading, error } = useCoursesBySubjects({
    subjectIds,
    subcategoryId,
    enabled: Boolean(categoryId && subjectIds.length),
  });

  const heroTitle = formatHeroTitle(categoryName, subcategoryName);
  const sectionTitle = formatCategoryHeading(categoryName, activeSubjectName);

  return (
    <>
      <HeaderSection />

      <CategoryCoursesHero title={heroTitle} description={HERO_DESCRIPTION} />

      <section className="bg-gradient-to-b from-[#ECE8F8] to-white pb-12 pt-8">
        <CategoryFeaturedCarousel courses={courses} />

        <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6">
          <SubjectFilterTabs
            subjects={subjects}
            activeSubjectId={activeSubjectId}
            onChange={handleSubjectChange}
            loading={subjectsLoading}
          />
        </div>

        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#1B1B1B] sm:text-[32px]">{sectionTitle}</h2>

          {loading || subjectsLoading ? (
            <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <li key={index} className="h-[420px] animate-pulse rounded-2xl bg-white/70" />
              ))}
            </ul>
          ) : error ? (
            <p className="mt-8 text-center text-red-600">{error}</p>
          ) : courses.length ? (
            <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <li key={course.id}>
                  <CourseCard course={course} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-8 text-center text-[#666666]">No courses found for this selection.</p>
          )}
        </div>
      </section>

      <FooterSection />
    </>
  );
}

export default CategoryCoursesPage;
